import React, { useCallback, useEffect, useState } from 'react';
import { FiCheck, FiGlobe, FiPlus, FiSave } from 'react-icons/fi';
import { api } from '../services/api';
import './AdminLocales.css';

const emptyLocale = {
  code: '',
  name: '',
  nativeName: '',
  direction: 'ltr',
  intlLocale: '',
  currencyCode: '',
  currencyLabel: '',
  paymentProvider: 'none',
  paymentEnabled: false,
  enabled: true,
  isDefault: false,
  sortOrder: 10,
};

const AdminLocales = () => {
  const [locales, setLocales] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyLocale);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');

  const loadLocales = useCallback(async () => {
    try {
      const response = await api.get('/api/locales/admin');
      setLocales(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setMessage(error.data?.message || error.message || 'دریافت زبان‌ها انجام نشد');
    }
  }, []);

  useEffect(() => { loadLocales(); }, [loadLocales]);

  const updateLocale = async (locale, patch) => {
    setBusy(locale.code);
    setMessage('');
    try {
      await api.put(`/api/locales/${locale.code}`, { ...locale, ...patch });
      setMessage('تنظیمات زبان ذخیره شد.');
      await loadLocales();
      window.dispatchEvent(new Event('haryad:locales-changed'));
    } catch (error) {
      setMessage(error.data?.message || error.message || 'ذخیره تنظیمات زبان انجام نشد');
    } finally {
      setBusy('');
    }
  };

  const createLocale = async (event) => {
    event.preventDefault();
    setBusy('create');
    setMessage('');
    try {
      await api.post('/api/locales', form);
      setForm(emptyLocale);
      setShowCreate(false);
      setMessage('زبان جدید ایجاد شد. اکنون تب ترجمه آن در محتوای سایت نمایش داده می‌شود.');
      await loadLocales();
      window.dispatchEvent(new Event('haryad:locales-changed'));
    } catch (error) {
      setMessage(error.data?.message || error.message || 'ایجاد زبان انجام نشد');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="admin-locales" dir="rtl">
      <header className="admin-locales__header">
        <div>
          <span><FiGlobe aria-hidden="true" /></span>
          <div>
            <h2>زبان‌های سایت</h2>
            <p>زبان‌های فعال در منوی سایت و فرم ترجمه محتوا نمایش داده می‌شوند.</p>
          </div>
        </div>
        <button type="button" onClick={() => setShowCreate((current) => !current)}>
          <FiPlus aria-hidden="true" /> افزودن زبان
        </button>
      </header>

      {showCreate && (
        <form className="admin-locales__create" onSubmit={createLocale}>
          <label>
            <span>کد استاندارد زبان</span>
            <input dir="ltr" value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value.toLowerCase() })} placeholder="مثلاً ar" required />
          </label>
          <label>
            <span>نام فارسی</span>
            <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="مثلاً عربی" required />
          </label>
          <label>
            <span>نام محلی</span>
            <input value={form.nativeName} onChange={(event) => setForm({ ...form, nativeName: event.target.value })} placeholder="مثلاً العربية" required />
          </label>
          <label>
            <span>جهت نوشتار</span>
            <select value={form.direction} onChange={(event) => setForm({ ...form, direction: event.target.value })}>
              <option value="rtl">راست به چپ</option>
              <option value="ltr">چپ به راست</option>
            </select>
          </label>
          <label>
            <span>Intl Locale</span>
            <input dir="ltr" value={form.intlLocale} onChange={(event) => setForm({ ...form, intlLocale: event.target.value })} placeholder="مثلاً ar-SA" required />
          </label>
          <label>
            <span>کد واحد پول</span>
            <input dir="ltr" value={form.currencyCode} onChange={(event) => setForm({ ...form, currencyCode: event.target.value.toUpperCase() })} placeholder="مثلاً USD" required />
          </label>
          <label>
            <span>عنوان واحد پول</span>
            <input value={form.currencyLabel} onChange={(event) => setForm({ ...form, currencyLabel: event.target.value })} placeholder="مثلاً دلار آمریکا" required />
          </label>
          <label>
            <span>ارائه‌دهنده پرداخت</span>
            <select value={form.paymentProvider} onChange={(event) => setForm({ ...form, paymentProvider: event.target.value, paymentEnabled: event.target.value === 'sepehr' && form.paymentEnabled })}>
              <option value="none">فعلاً بدون درگاه</option>
              <option value="sepehr">سپهر</option>
            </select>
          </label>
          <button type="submit" disabled={busy === 'create'}><FiSave /> {busy === 'create' ? 'در حال ایجاد...' : 'ایجاد زبان'}</button>
        </form>
      )}

      {message && <p className="admin-locales__message">{message}</p>}

      <div className="admin-locales__list">
        {locales.map((locale) => (
          <article key={locale.code} className={!locale.enabled ? 'is-disabled' : ''}>
            <div className="admin-locales__identity">
              <strong>{locale.name}</strong>
              <span dir={locale.direction}>{locale.nativeName}</span>
              <code>{locale.code}</code>
            </div>
            <div className="admin-locales__meta">
              <span>{locale.direction === 'rtl' ? 'راست به چپ' : 'چپ به راست'}</span>
              <span dir="ltr">{locale.intlLocale}</span>
            </div>
            <div className="admin-locales__market">
              <label>
                <span>کد پول</span>
                <input
                  dir="ltr"
                  value={locale.currencyCode || ''}
                  onChange={(event) => setLocales((items) => items.map((item) => (
                    item.code === locale.code ? { ...item, currencyCode: event.target.value.toUpperCase() } : item
                  )))}
                />
              </label>
              <label>
                <span>عنوان پول</span>
                <input
                  value={locale.currencyLabel || ''}
                  onChange={(event) => setLocales((items) => items.map((item) => (
                    item.code === locale.code ? { ...item, currencyLabel: event.target.value } : item
                  )))}
                />
              </label>
              <label>
                <span>درگاه</span>
                <select
                  value={locale.paymentProvider || 'none'}
                  onChange={(event) => setLocales((items) => items.map((item) => (
                    item.code === locale.code ? { ...item, paymentProvider: event.target.value } : item
                  )))}
                >
                  <option value="none">بدون درگاه</option>
                  <option value="sepehr">سپهر</option>
                </select>
              </label>
              <label className="admin-locales__payment-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(locale.paymentEnabled)}
                  disabled={(locale.paymentProvider || 'none') === 'none'}
                  onChange={(event) => setLocales((items) => items.map((item) => (
                    item.code === locale.code ? { ...item, paymentEnabled: event.target.checked } : item
                  )))}
                />
                درگاه فعال
              </label>
              <button type="button" disabled={busy === locale.code} onClick={() => updateLocale(locale, {})}>
                <FiSave /> ذخیره بازار
              </button>
            </div>
            <div className="admin-locales__actions">
              <label>
                <input
                  type="checkbox"
                  checked={locale.enabled}
                  disabled={busy === locale.code || locale.isDefault}
                  onChange={(event) => updateLocale(locale, { enabled: event.target.checked })}
                />
                فعال
              </label>
              <button
                type="button"
                className={locale.isDefault ? 'is-default' : ''}
                disabled={busy === locale.code || locale.isDefault}
                onClick={() => updateLocale(locale, { isDefault: true, enabled: true })}
              >
                <FiCheck /> {locale.isDefault ? 'زبان پیش‌فرض' : 'انتخاب به‌عنوان پیش‌فرض'}
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default AdminLocales;
